import React, { Component } from 'react';
import { connect } from 'react-redux';
import { submit } from 'redux-form';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import Calendar from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';
import throttle from 'lodash/throttle';

import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import MultiSelectDropDown from 'components/ui/MultiSelectDropDown';
import {
  getTicket,
  getAllMaintenanceTickets,
  getFilteredMaintenanceTickets,
  deleteTicket,
} from 'actions/maintenanceTicketActions';
import {
  createOrUpdateMaintenanceTicket,
  createOrUpdateMaintenanceTask,
  deleteTask,
  getAssets,
  getListOfMaintenanceStaff,
  getMaintenanceCategories,
  getMaintenanceLocations,
} from 'actions/maintenanceActions';
import { getProperties, getUnits } from 'actions/landlordPropertiesActions';
import { getGroupedVendors } from 'actions/vendorsActions';
import AddAndViewMaintenanceTickets from '../components/AddAndViewMaintenanceTickets';
import UnscheduledTicket from './UnscheduledTicket';
import TicketTimePickerDialog from './TicketTimePickerDialog';
import { getCompanyPreferences } from 'actions/companyDetailsActions';
import * as formatter from 'helpers/Formatter';
import MediaQuery from 'react-responsive';
import { getTranslatedString } from 'helpers/i18n';

import AntDrawer from 'components/ui/Drawer';
import { RenderIfToggleOn, RenderIfToggleOff } from 'components/shared/ToggleRenderer';
import { featureEnabled } from 'helpers/ToggleBot';
import TicketCreating from 'components/landlord_app/maintenance/tickets/TicketCreating';
import TicketViewing from 'components/landlord_app/maintenance/tickets/TicketViewing/TicketViewing';

const styles = {
  header: {
    padding: '1rem 1rem 2rem',
    borderBottom: '1px solid rgba(128, 128, 128, 0.3)',
    overflow: 'hidden',
  },
  container: {
    display: 'flex',
    height: 'calc(100% - 133px)',
    paddingBottom: '60px',
  },
  leftBar: {
    width: '250px',
    minHeight: '100%',
    borderRight: '1px solid rgba(128, 128, 128, 0.3)',
    padding: '1rem',
    overflowY: 'scroll',
  },
  page: {
    flexGrow: '1',
    padding: '1rem',
  },
  mobileContainer: {
    display: 'flex',
    height: 'calc(100% - 133px)',
    width: '100vw',
    flexDirection: 'column',
  },
  mobileLeftBar: {
    minHeight: '300px',
    borderBottom: '1px solid rgba(128, 128, 128, 0.3)',
    padding: '1rem',
    overflowY: 'scroll',
  },
  mobilePage: {
    flexGrow: '1',
    paddingBottom: '60px',
  },
};

const DnDCalendar = withDragAndDrop(Calendar, { backend: false });

let defaultMessages = {
  date: getTranslatedString('landlordPortal.landlordTicketScheduler.date'),
  time: getTranslatedString('landlordPortal.landlordTicketScheduler.time'),
  event: getTranslatedString('landlordPortal.landlordTicketScheduler.event'),
  allDay: getTranslatedString('landlordPortal.landlordTicketScheduler.allDay'),
  week: getTranslatedString('landlordPortal.landlordTicketScheduler.week'),
  work_week: getTranslatedString('landlordPortal.landlordTicketScheduler.work_week'),
  day: getTranslatedString('landlordPortal.landlordTicketScheduler.day'),
  month: getTranslatedString('landlordPortal.landlordTicketScheduler.month'),
  previous: getTranslatedString('landlordPortal.landlordTicketScheduler.previous'),
  next: getTranslatedString('landlordPortal.landlordTicketScheduler.next'),
  yesterday: getTranslatedString('landlordPortal.landlordTicketScheduler.yesterday'),
  tomorrow: getTranslatedString('landlordPortal.landlordTicketScheduler.tomorrow'),
  today: getTranslatedString('landlordPortal.landlordTicketScheduler.today'),
  agenda: getTranslatedString('landlordPortal.landlordTicketScheduler.agenda'),
  noEventsInRange: getTranslatedString('landlordPortal.landlordTicketScheduler.noEventsInRange'),
  showMore: (total) =>
    `+${total} ${getTranslatedString('landlordPortal.landlordTicketScheduler.more')}`,
};

class LandlordTicketScheduler extends Component {
  constructor(props) {
    super(props);
    this.state = {
      drawerIsOpen: false,
      showDrawer: false,
      createTicketDrawerOpen: false,
      viewTicketDrawerOpen: false,
      selectedPropertyIds: [],
      events: [],
      unscheduledTickets: [],
      loadingTickets: false,
      timePickerForTicketId: null,
      initScheduledStartsAt: null,
    };
  }

  containerRef = React.createRef();
  lastTicketRef = React.createRef();
  lastTicketId;

  handleScroll = (e) => {
    const scrollStrengthThreshold = -100;
    const scrollElem = this.containerRef.current;
    const maxScrollDistance = scrollElem.scrollHeight - scrollElem.clientHeight;

    if (e.wheelDelta < scrollStrengthThreshold && scrollElem.scrollTop > maxScrollDistance - 10) {
      this.throttledFetch();
    }
  };

  throttledFetch = throttle(
    () => {
      this.getAllMaintenanceTickets(false);
    },
    2000,
    { trailing: false }
  );

  componentWillUnmount() {
    this.containerRef.current.removeEventListener('wheel', this.handleScroll);
  }

  componentDidUpdate() {
    if (this.lastTicketRef.current && this.lastTicketId !== this.lastTicketRef.current.id) {
      this.lastTicketRef.current.scrollIntoView({ behavior: 'smooth' });
      this.lastTicketId = this.lastTicketRef.current.id;
    }
  }

  componentDidMount = () => {
    this.props.getProperties({ for_multiselect_single_unit: true });
    this.props.getCompanyPreferences();
    this.containerRef.current.addEventListener('wheel', this.handleScroll);
  };

  getAllMaintenanceTickets = async (loadingTickets = true) => {
    this.setState(
      {
        loadingTickets,
      },
      async () => {
        const filterOptions = {
          property_ids: this.state.selectedPropertyIds,
        };
        const subset = 'not_fixed_or_closed';
        await this.props.getFilteredMaintenanceTickets(subset, this.state.offset, filterOptions);

        this.setState({
          offset: this.props.maintenanceTickets.length,
        });

        let newUnscheduledTickets = [...this.state.unscheduledTickets];
        if (this.state.selectedPropertyIds) {
          this.loadPropertyEventsOnCalendar(this.state.selectedPropertyIds);
          newUnscheduledTickets = this.props.maintenanceTickets.filter(
            (ticket) =>
              !ticket.scheduled && this.state.selectedPropertyIds.includes(ticket.property_id)
          );
        }
        this.setState({
          loadingTickets: false,
          unscheduledTickets: newUnscheduledTickets,
        });
      }
    );
  };

  reloadTableData = async () => {
    const filterOptions = { property_ids: this.state.selectedPropertyIds };
    const subset = 'not_fixed_or_closed';
    await this.props.getTicket(this.props.ticketDetails.id);
    return this.props.getFilteredMaintenanceTickets(subset, 0, filterOptions).then(() => {
      this.setState({ offset: 0 }, () => {
        let newUnscheduledTickets = [...this.state.unscheduledTickets];
        if (this.state.selectedPropertyIds) {
          this.loadPropertyEventsOnCalendar(this.state.selectedPropertyIds);
          newUnscheduledTickets = this.props.maintenanceTickets.filter(
            (ticket) =>
              !ticket.scheduled && this.state.selectedPropertyIds.includes(ticket.property_id)
          );
        }
        this.setState({
          loadingTickets: false,
          unscheduledTickets: newUnscheduledTickets,
        });
      });
    });
  };

  loadPropertyEventsOnCalendar = (propertyIds) => {
    const scheduledTicketsAsEvents = this.props.maintenanceTickets
      .filter((ticket) => ticket.scheduled && propertyIds.includes(ticket.property_id))
      .map((ticket) => {
        return {
          id: ticket.id,
          title: ticket.subject,
          start: new Date(ticket.scheduled_starts_at),
          end: new Date(
            moment(ticket.scheduled_starts_at).add(parseFloat(ticket.scheduled_duration), 'hours')
          ),
        };
      });
    this.setState({
      events: scheduledTicketsAsEvents,
    });
  };

  toggleAddTicket = () => {
    if (!featureEnabled('ui.maintenance.new_tickets_ui')) {
      this.setState((prevState) => ({ drawerIsOpen: !prevState.drawerIsOpen }));
    } else {
      this.toggleCreateTicketDrawer();
    }
  };

  handlePropertySelect = (propertyIds) => {
    const maintenanceTickets = this.props.maintenanceTickets.filter(
      (ticket) => !ticket.scheduled && propertyIds.includes(ticket.property_id)
    );

    this.setState(
      {
        selectedPropertyIds: propertyIds,
        unscheduledTickets: maintenanceTickets,
      },
      () => {
        this.getAllMaintenanceTickets();
        this.props.getListOfMaintenanceStaff(propertyIds);
        this.loadPropertyEventsOnCalendar(propertyIds);
      }
    );
  };

  addTicket = (formData) => {
    return this.props.createOrUpdateMaintenanceTicket(formData).then(() => {
      this.toggleAddTicket();
      this.getAllMaintenanceTickets();
    });
  };

  toggleOldTicketViewDrawer = async (maintenanceTicketID) => {
    if (this.state.showDrawer || !maintenanceTicketID) {
      this.setState({ showDrawer: false });
      return;
    }

    await this.props.getTicket(maintenanceTicketID);
    let assetParams = {
      property_id: this.props.ticketDetails.property_id,
      for_multiselect: true,
    };

    const unitId = this.props.ticketDetails.unit_id;

    if (unitId) {
      assetParams = { ...assetParams, unit_id: unitId };
    }

    this.props.getAssets(assetParams);
    this.props.getUnits(this.props.ticketDetails.property_id, { for_multiselect: true });
    this.props.getMaintenanceCategories(this.props.ticketDetails.property_id);
    this.props.getMaintenanceLocations(this.props.ticketDetails.property_id);
    this.props.getGroupedVendors();
    this.setState({
      showDrawer: true,
    });
    this.props.getGroupedVendors();
    return;
  };

  openTicketViewDrawer = async (ticketID) => {
    if (!featureEnabled('ui.maintenance.new_tickets_ui')) {
      this.toggleOldTicketViewDrawer(ticketID);
      return;
    }

    await this.props.getTicket(ticketID);
    this.setState({ viewTicketDrawerOpen: true });
  };

  closeTicketViewDrawer = () => {
    if (!featureEnabled('ui.maintenance.new_tickets_ui')) {
      this.toggleOldTicketViewDrawer();
      return;
    }

    this.setState({ viewTicketDrawerOpen: false });
  };

  toggleCreateTicketDrawer = () => {
    this.setState((prevState) => ({
      createTicketDrawerOpen: !prevState.createTicketDrawerOpen,
    }));
  };

  updateTicket = (formData, ticketID) => {
    if (ticketID) {
      return this.props.createOrUpdateMaintenanceTicket(formData, ticketID).then(() => {
        this.getAllMaintenanceTickets();
      });
    }

    return this.props
      .createOrUpdateMaintenanceTicket(formData, this.props.ticketDetails.id)
      .then(() => {
        this.getAllMaintenanceTickets();
        this.props.getTicket(this.props.ticketDetails.id);
      });
  };

  deleteTicket = () => {
    return this.props.deleteTicket(this.props.ticketDetails.id).then(() => {
      this.closeTicketViewDrawer();
      this.getAllMaintenanceTickets();
    });
  };

  createOrUpdateTask = async (task, taskID) => {
    await this.props.createOrUpdateMaintenanceTask(task, taskID, this.props.ticketDetails.id);
    this.getAllMaintenanceTickets();
  };

  deleteTask = async (taskId) => {
    await this.props.deleteTask(taskId, this.props.ticketDetails.id);
    this.getAllMaintenanceTickets();
  };

  getSelectedPropertyTickets = () => {
    return this.props.maintenanceTickets.filter((ticket) => {
      return (
        this.state.selectedPropertyIds &&
        this.state.selectedPropertyIds.includes(ticket.property_id)
      );
    });
  };

  moveEvent = ({ event, start, end }) => {
    const { events } = this.state;
    const idx = events.indexOf(event);
    if (idx === -1) {
      this.setState({
        initScheduledStartsAt: start,
        timePickerForTicketId: event.id,
      });
    } else {
      const nextEvents = [...events];
      const updatedEvent = { ...event, start, end };
      this.scheduleTicket(event.id, true, start, end);
      nextEvents.splice(idx, 1, updatedEvent);
      this.setState({
        events: nextEvents,
      });
    }
  };

  resizeEvent = (_resizeType, { event, start, end }) => {
    this.scheduleTicket(event.id, true, start, end);
    const { events } = this.state;
    const nextEvents = events.map((existingEvent) => {
      if (existingEvent.id === event.id) {
        return { ...existingEvent, start, end };
      } else {
        return existingEvent;
      }
    });
    this.setState({
      events: nextEvents,
    });
  };

  // This returns closest permitted duration value between two dates in hours
  getDurationHours = (start, end) => {
    let durations = Array.from(Array(24).keys()).map((val) => (val + 1) / 2);
    durations.concat([24, 48, 72]);
    let duration = Math.abs(end.getTime() - start.getTime()) / 3600000;

    const diffs = durations.map((item) => Math.abs(duration - item));
    const idx = diffs.indexOf(Math.min(...diffs));
    return durations[idx];
  };

  scheduleTicket = (ticketId, scheduled, start, end, assignedTo) => {
    let updatedTicket = this.props.maintenanceTickets.find((ticket) => ticket.id === ticketId);
    if (updatedTicket) {
      updatedTicket.scheduled = scheduled;
      updatedTicket.scheduled_starts_at = start;
      updatedTicket.scheduled_starts_at_date = formatter.toISO8601DateString(start);
      updatedTicket.scheduled_starts_at_time = formatter.toTimeString(start);
      updatedTicket.scheduled_duration = this.getDurationHours(start, end);
      if (assignedTo) {
        updatedTicket.assign = assignedTo;
      }
    }
    this.updateTicket(updatedTicket, ticketId);
  };
  closeTimePickerDialog = () => {
    this.setState({
      timePickerForTicketId: null,
    });
  };
  eventStyleGetter = (event, _start, _end, _isSelected) => {
    let style = null;
    const ticket = this.props.maintenanceTickets.find((ticket) => ticket.id === event.id);
    const staffId = ticket && ticket.assigned_to_id;
    if (staffId === null) {
      style = {
        background: '#e2e2e2',
        border: '1px solid rgba(128, 128, 128, 0.3)',
        color: 'black',
      };
    } else {
      style = {
        backgroundColor: this.getAColor(staffId),
        color: 'white',
      };
    }
    return { style: style };
  };

  render() {
    // locale for big calendar
    moment.locale(this.props.languageCode);
    Calendar.momentLocalizer(moment);

    return (
      <div className="body" role="main">
        <div style={styles.header}>
          <HeaderWithPeriod
            headerText={getTranslatedString(
              'landlordPortal.landlordTicketScheduler.ticketScheduler'
            )}
            actionButtonLabel={getTranslatedString(
              'landlordPortal.landlordTicketScheduler.addTicket'
            )}
            toggleDialog={this.toggleAddTicket}
          />
          <div style={{ width: '350px', float: 'right', maxHeight: '94px', overflow: 'scroll' }}>
            <MultiSelectDropDown
              name="property"
              data={this.props.properties}
              label={getTranslatedString('landlordPortal.landlordTicketScheduler.selectProperty')}
              menuStyle={{ minWidth: '250px', fontSize: '14px' }}
              style={{ fontSize: '15px', width: '350px' }}
              fullWidth
              value={this.state.selectedPropertyIds}
              onChange={this.handlePropertySelect}
            />
          </div>
          {this._renderOldAddAndViewTicketDialogs()}
          {this._renderCreateTicketDrawer()}
          {this._renderViewTicketDrawer()}
          {this.renderStaffLegend()}
        </div>
        {this._renderMobileView()}
        {this._renderNonMobileView()}
      </div>
    );
  }

  _renderOldAddAndViewTicketDialogs = () => (
    <RenderIfToggleOff toggle="ui.maintenance.new_tickets_ui">
      <AddAndViewMaintenanceTickets
        addTicket={this.addTicket}
        drawerIsOpen={this.state.drawerIsOpen}
        toggleAddTicket={this.toggleAddTicket}
        showDrawer={this.state.showDrawer}
        toggleDrawer={this.toggleOldTicketViewDrawer}
        updateTicket={this.updateTicket}
        deleteAction={this.deleteTicket}
        createOrUpdateTask={this.createOrUpdateTask}
        deleteTask={this.deleteTask}
        maintenanceTicket={this.props.ticketDetails}
        submitMaintenanceTicket={() => this.props.submit('add_ticket_form')}
        submittingMaintenance={this.props.submitting}
        companyPreferences={this.props.companyPreferences}
      />
    </RenderIfToggleOff>
  );

  _renderCreateTicketDrawer = () => (
    <RenderIfToggleOn toggle="ui.maintenance.new_tickets_ui">
      <AntDrawer
        maskClosable={false}
        keyboard
        visible={this.state.createTicketDrawerOpen}
        onClose={this.toggleCreateTicketDrawer}
        closable={false}
        width={600}
      >
        {this.state.createTicketDrawerOpen && (
          <TicketCreating
            handleClose={this.toggleCreateTicketDrawer}
            onSubmissionComplete={this.getAllMaintenanceTickets}
          />
        )}
      </AntDrawer>
    </RenderIfToggleOn>
  );

  _renderViewTicketDrawer = () => (
    <RenderIfToggleOn toggle="ui.maintenance.new_tickets_ui">
      <AntDrawer
        maskClosable={false}
        keyboard
        visible={this.state.viewTicketDrawerOpen}
        onClose={this.closeTicketViewDrawer}
        closable={false}
        width={600}
      >
        {this.state.viewTicketDrawerOpen && (
          <TicketViewing
            handleClose={this.closeTicketViewDrawer}
            selectedTicket={this.props.ticketDetails}
            onSubmissionComplete={this.reloadTableData}
          />
        )}
      </AntDrawer>
    </RenderIfToggleOn>
  );

  _renderMobileView = () => {
    return (
      <MediaQuery maxDeviceWidth={750}>
        <div style={styles.mobileContainer}>
          <div style={styles.mobileLeftBar} ref={this.containerRef}>
            <h4>
              {getTranslatedString('landlordPortal.landlordTicketScheduler.unscheduledTickets')}
            </h4>
            {this.renderUnscheduledTasks()}
          </div>
          <div style={styles.mobilePage}>
            <DnDCalendar
              messages={defaultMessages}
              defaultDate={new Date()}
              defaultView="week"
              events={this.state.events}
              onEventDrop={this.moveEvent}
              onEventResize={this.resizeEvent}
              onSelectEvent={(event) => this.openTicketViewDrawer(event.id)}
              eventPropGetter={this.eventStyleGetter}
              resizable
              selectable
            />
            <TicketTimePickerDialog
              open={this.state.timePickerForTicketId !== null}
              initScheduledStartsAt={this.state.initScheduledStartsAt}
              ticketId={this.state.timePickerForTicketId}
              scheduleTicket={this.scheduleTicket}
              closeDialog={this.closeTimePickerDialog}
              maintenanceStaff={this.props.maintenanceStaff}
              companyPreferences={this.props.companyPreferences}
            />
          </div>
        </div>
      </MediaQuery>
    );
  };

  _renderNonMobileView = () => {
    return (
      <MediaQuery minDeviceWidth={751}>
        <div style={styles.container}>
          <div style={styles.leftBar} ref={this.containerRef}>
            <h4>
              {getTranslatedString('landlordPortal.landlordTicketScheduler.unscheduledTickets')}
            </h4>
            {this.renderUnscheduledTasks()}
          </div>
          <div style={styles.page}>
            <DnDCalendar
              messages={defaultMessages}
              defaultDate={new Date()}
              defaultView="week"
              views={['month', 'week', 'day']}
              events={this.state.events}
              onEventDrop={this.moveEvent}
              onEventResize={this.resizeEvent}
              onSelectEvent={(event) => this.openTicketViewDrawer(event.id)}
              eventPropGetter={this.eventStyleGetter}
              resizable
              selectable
            />
            <TicketTimePickerDialog
              open={this.state.timePickerForTicketId !== null}
              initScheduledStartsAt={this.state.initScheduledStartsAt}
              ticketId={this.state.timePickerForTicketId}
              scheduleTicket={this.scheduleTicket}
              closeDialog={this.closeTimePickerDialog}
              maintenanceStaff={this.props.maintenanceStaff}
              companyPreferences={this.props.companyPreferences}
            />
          </div>
        </div>
      </MediaQuery>
    );
  };

  renderUnscheduledTasks = () => {
    const { loadingTickets } = this.state;
    if (loadingTickets) {
      return <Skeleton height={50} count={5} />;
    }
    let tickets = this.state.unscheduledTickets;
    if (tickets.length === 0) {
      return (
        <div>{getTranslatedString('landlordPortal.landlordTicketScheduler.noTicketsFound')}</div>
      );
    }
    return tickets.map((ticket, index) => {
      let fgColor, captionColor;
      if (ticket.assigned_to_id === null) {
        fgColor = 'black';
        captionColor = 'rgba(0,0,0,0.4)';
      } else {
        fgColor = 'white';
        captionColor = 'rgba(255,255,255,0.6)';
      }
      return (
        <UnscheduledTicket
          key={`mt_${ticket.id}`}
          ticket={ticket}
          bgColor={this.getAColor(ticket.assigned_to_id)}
          fgColor={fgColor}
          captionColor={captionColor}
          goToTicket={this.openTicketViewDrawer}
          ref={index === tickets.length - 1 ? this.lastTicketRef : null}
        />
      );
    });
  };

  getAColor = (staffId) => {
    const colors = ['#3748ac', '#e0bc39', '#f2471c', '#a3144d', '#0e753b', '#8321a0'];
    const index = this.props.maintenanceStaff.findIndex((staff) => staff.value === staffId);
    return index !== -1 ? colors[index % colors.length] : 'rgb(248, 248, 248)';
  };

  renderStaffLegend = () => {
    return this.props.maintenanceStaff.map((staff) => {
      if (staff.label === 'n/a') return null;
      return (
        <div
          key={`staff_${staff.value}`}
          style={{
            backgroundColor: this.getAColor(staff.value),
            fontSize: '10px',
            color: 'white',
            display: 'inline-block',
            marginRight: '5px',
            borderRadius: '100px',
            padding: '2px 12px',
          }}
        >
          {staff.label}
        </div>
      );
    });
  };
}
export default connect(
  (store) => ({
    properties: store.landlordProperties.properties,
    maintenanceTickets: store.maintenanceTicket.maintenanceTickets,
    ticketDetails: store.maintenanceTicket.ticketDetails,
    maintenanceStaff: store.maintenanceReducer.maintenance,
    companyPreferences: store.companyDetails.companyPreferences,
    languageCode: store.locale.currentLanguage,
  }),
  {
    getAllMaintenanceTickets,
    getFilteredMaintenanceTickets,
    createOrUpdateMaintenanceTicket,
    getProperties,
    submit,
    getTicket,
    getUnits,
    getGroupedVendors,
    deleteTicket,
    createOrUpdateMaintenanceTask,
    deleteTask,
    getAssets,
    getListOfMaintenanceStaff,
    getCompanyPreferences,
    getMaintenanceCategories,
    getMaintenanceLocations,
  }
)(DragDropContext(HTML5Backend)(LandlordTicketScheduler));
